import { AnswerBox } from '@/components/seo/answer-box';
import { faqJsonLd, FaqSection } from '@/components/seo/faq-list';
import { GuideProductCard, GuideSectionCard } from '@/components/seo/guide-section';
import { HourlyRateCalculator } from '@/components/seo/hourly-rate-calculator';
import { breadcrumbJsonLd, FinalCta, Reveal, SeeAlso, SeoSection } from '@/components/seo/seo-blocks';
import { SeoHero } from '@/components/seo/seo-hero';
import { SeoPageShell } from '@/components/seo/seo-page-shell';
import { SeoHead, SITE_URL } from '@/components/seo-head';
import { findGuidePage, GUIDE_PAGES, GUIDE_PRODUCT_HEADING } from '@/content/seo/guides';
import { findSegmentPage } from '@/content/seo/segments';
import { pageTitle } from '@/lib/seo/meta';
import { Href, useLocalSearchParams } from 'expo-router';
import NotFoundScreen from '../+not-found';
import React from 'react';
import { View } from 'react-native';

/** Guias que levam uma ferramenta embutida, logo depois da resposta. */
const GUIDE_TOOLS: Record<string, React.FC> = {
  'quanto-cobrar-por-hora': HourlyRateCalculator,
};

export async function generateStaticParams(): Promise<{ slug: string }[]> {
  return GUIDE_PAGES.map((page) => ({ slug: page.slug }));
}

const formatDate = (iso: string) => {
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};

/**
 * Guia (`/guias/[slug]`): resposta curta no topo, uma parte por cartão (a
 * pergunta, a resposta curta, vistos, mensagem pronta e fontes da lei), depois
 * "Como a Controlapag faz isso?" separado do conselho, e FAQ. Tudo no HTML
 * pré-renderizado. Conteúdo em `content/seo/guides.ts`.
 */
export default function GuideScreen() {
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const page = findGuidePage(String(slug ?? ''));

  if (!page) return <NotFoundScreen />;

  const path = `/guias/${page.slug}`;
  const Tool = GUIDE_TOOLS[page.slug];
  const segments = page.segments
    .map((s) => findSegmentPage(s))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));
  const related = page.related
    .map((s) => findGuidePage(s))
    .filter((s): s is NonNullable<typeof s> => Boolean(s));

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: page.h1,
      description: page.description,
      inLanguage: 'pt-BR',
      image: `${SITE_URL}/api/og-image`,
      datePublished: page.publishedAt,
      dateModified: page.updatedAt,
      mainEntityOfPage: `${SITE_URL}${path}`,
      author: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Controlapag' },
      publisher: { '@type': 'Organization', '@id': `${SITE_URL}/#organization`, name: 'Controlapag' },
    },
    faqJsonLd(page.faq),
    breadcrumbJsonLd(SITE_URL, [
      { name: 'Início', path: '/' },
      { name: 'Guias', path: '/guias' },
      { name: page.h1, path },
    ]),
  ];

  return (
    <SeoPageShell
      screenTitle={page.h1}
      dockLabel="Clientes, agenda e cobranças num lugar só"
      head={<SeoHead title={pageTitle(page.title)} description={page.description} path={path} jsonLd={jsonLd} />}
    >
      <SeoHero
        width="narrow"
        crumbs={[
          { label: 'Início', href: '/' },
          { label: 'Guias', href: '/guias' as Href },
        ]}
        meta={`Por Equipe Controlapag · atualizado em ${formatDate(page.updatedAt)}`}
        title={page.h1}
      >
        <View className="mx-auto w-full max-w-4xl">
          <AnswerBox text={page.answer} />
        </View>
      </SeoHero>

      <SeoSection narrow flushTop>
        <View className="gap-4">
          {Tool ? (
            <Reveal>
              <Tool />
            </Reveal>
          ) : null}
          {page.sections.map((section, index) => (
            <GuideSectionCard key={section.heading} section={section} index={index} />
          ))}
          <GuideProductCard heading={GUIDE_PRODUCT_HEADING} product={page.product} />
        </View>
      </SeoSection>

      <FaqSection
        items={page.faq}
        footer={
          <SeeAlso
            links={related.map((item) => ({ label: item.h1, href: `/guias/${item.slug}` as Href }))}
            chips={[
              ...segments.map((item) => ({ label: item.label, href: `/para/${item.slug}` as Href })),
              { label: 'Todos os guias', href: '/guias' as Href },
            ]}
          />
        }
      />

      <FinalCta title="Organize clientes, agenda e cobranças num lugar só" />
    </SeoPageShell>
  );
}
